import moment from 'moment';
import React from 'react';

import { Chip, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import Payment from '@material-ui/icons/Payment';
import Visibility from '@material-ui/icons/Visibility';

import Button from 'creative-components/CustomButtons/Button';
import ReactTable from 'creative-components/ReactTable/ReactTable';

import InvoicePaymentModal from 'components/Billing/InvoicePaymentModal';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';

import { formatCents } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  tableContainer: { overflowX: 'auto', overflowY: 'auto' },
  table: { width: '100%', height: '100%' },
  amountCell: { fontWeight: 600 },
  actionsCell: { display: 'flex', alignItems: 'center', gap: '4px' },
  statusCell: { display: 'inline-flex' },
  statusChip: {
    height: '24px', borderRadius: '12px', fontWeight: 700, fontSize: '11px', letterSpacing: '0.04em', textTransform: 'uppercase',
  },
  paidChip: { backgroundColor: '#E8F5E9', color: '#1B5E20' },
  unpaidChip: { backgroundColor: '#FFF3E0', color: '#A44200' },
  uncollectibleChip: { backgroundColor: '#FDECEA', color: '#B42318' },
  unknownChip: { backgroundColor: '#ECEFF1', color: '#455A64' },
  empty: {
    padding: theme.spacing(5, 2), textAlign: 'center', color: theme.palette.text.secondary, border: `1px solid ${theme.palette.divider}`, borderRadius: 8,
  },
}));

const statusLabels = { open: 'Unpaid', paid: 'Paid', uncollectible: 'Uncollectible' };

const dateColumn = (header) => ({
  Header: header,
  accessor: 'created',
  Cell: ({ cell: { value } }) => moment(value).format('MMM D, YYYY'),
  sortType: 'datetime',
});

const columns = [
  dateColumn('Invoice Date'),
  { Header: 'Invoice Number', accessor: 'number' },
  { Header: 'Payment Method', accessor: 'paymentMethod' },
  { Header: 'Status', accessor: 'status' },
  { Header: 'Amount', accessor: 'amount' },
  { Header: '', accessor: 'actions' },
];

const InvoicesTable = ({
  invoices, allowRetry = false, onRetrySuccess,
}) => {
  const classes = useStyles();
  const [selectedInvoice, setSelectedInvoice] = React.useState(null);

  if (!invoices) return <LoadingIndicator />;
  if (!invoices.length) return <div className={classes.empty}>No invoices to show.</div>;

  const data = invoices.map((invoice) => {
    const {
      number, status, total, amountDue, amountPaid, amountRefunded,
      hostedInvoiceUrl, created, paymentMethod,
    } = invoice;
    const statusClass = { paid: classes.paidChip, open: classes.unpaidChip, uncollectible: classes.uncollectibleChip }[status] || classes.unknownChip;
    const creditsAppliedCents = typeof amountDue === 'number' ? total - amountDue : null;
    const amountDetails = [];
    if (status === 'paid') {
      if (creditsAppliedCents) amountDetails.push(`Credits ${formatCents(creditsAppliedCents)}`);
      if (typeof amountPaid === 'number' && amountPaid !== total) amountDetails.push(`Paid ${formatCents(amountPaid)}`);
      if (amountRefunded) amountDetails.push(`Refunded ${formatCents(amountRefunded)}`);
    }

    return {
      number: <b>{number}</b>,
      paymentMethod: paymentMethod ? `${paymentMethod.brand?.toUpperCase()} •${paymentMethod.last4}` : 'Unavailable',
      status: <span className={classes.statusCell}><Chip label={statusLabels[status] || status} size="small" className={`${classes.statusChip} ${statusClass}`} /></span>,
      amount: <div className={classes.amountCell}>{formatCents(total)}</div>,
      created: moment(created * 1000).toDate(),
      actions: (
        <div className={classes.actionsCell}>
          {allowRetry && status === 'open' && (
            <Button round outlined color="orange" size="sm" aria-label={`Pay invoice ${number}`} onClick={() => setSelectedInvoice(invoice)}>
              <Payment />
              {' '}
              Pay invoice
            </Button>
          )}
          {amountDetails.length > 0 && (
            <Tooltip title={<div>{amountDetails.map((detail) => <div key={detail}>{detail}</div>)}</div>} placement="top">
              <Button justIcon round simple color="dark" aria-label={`Payment details for invoice ${number}`}><InfoOutlined /></Button>
            </Tooltip>
          )}
          <Tooltip title="View invoice" placement="top">
            <Button justIcon round simple href={hostedInvoiceUrl} color="dark" target="_blank" rel="noopener noreferrer" aria-label={`View invoice ${number}`}><Visibility /></Button>
          </Tooltip>
        </div>
      ),
    };
  });

  return (
    <>
      <div className={classes.tableContainer}>
        <div className={classes.table}><ReactTable columns={columns} data={data} initialSortBy={[]} /></div>
      </div>
      <InvoicePaymentModal
        invoice={selectedInvoice}
        open={!!selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
        onPaid={onRetrySuccess}
        updateFuturePaymentMethod
      />
    </>
  );
};

export default InvoicesTable;
